import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Row, Col, message, Spin } from 'antd';
import { BaseBreadcrumb } from '@app/components/common/BaseBreadcrumb/BaseBreadcrumb';
import { PageTitle } from '@app/components/common/PageTitle/PageTitle';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import ReportSelector from './ReportTypeSelector';
import type { ReportType } from './ReportTypeSelector';
import { Modal, Button, Form, Select, DatePicker } from 'antd';
import { useState } from 'react';
import ReportFilters from '@app/components/apps/Reports/ReportFilters';
import { getReportApiByKey } from '@app/api/reports.api';
import { REPORT_MODEL_MAP } from '@app/components/apps/Reports/ReportsConfig';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';

const breadcrumbMap: Record<string, string> = {
  lab: 'Lab Reports',
  'pr&license': 'PR & License Reports',
  'import&export': 'Import & Export Reports',
  finance: 'Financial Reports',
  followup: 'Follow-up Reports',
};

const AllReportTypes: React.FC = () => {
  const { setBreadcrumb } = useHeader();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.replace('/', '');

  const repType = (localStorage.getItem('selected_rep_type') as ReportType) || 'lab';
  const breadcrumbName = breadcrumbMap[repType] || 'Reports';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReportKey, setSelectedReportKey] = useState('');
  const [filterOptions, setFilterOptions] = useState<any>({});
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const handleError = useApiErrorHandler();

  useEffect(() => {
    setBreadcrumb(
      <BaseBreadcrumb
        routes={[{ path: `/${currentPath}`, breadcrumbName: `${breadcrumbName}` }]}
        style={{ marginBottom: '15px' }}
      />,
    );

    return () => setBreadcrumb(undefined);
  }, [repType, setBreadcrumb, navigate]);

  const handleReportSelect = async (key: string) => {
    setSelectedReportKey(key);
    form.resetFields();
    setIsModalOpen(true);
    setLoading(true);

    const api = getReportApiByKey(key);
    if (!api) {
      message.error('No API mapping found for this report key.');
      setIsModalOpen(false);
      setLoading(false);
      return;
    }

    try {
      const data = await api.getFilters();
      const transformed = transformApiToFilterOptions(data);
      setFilterOptions(transformed);
    } catch (err) {
      handleError(err, { redirect: false });
      setIsModalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    sessionStorage.removeItem('report_data');
    form
      .validateFields()
      .then((values) => {
        const { dateRange, ...rest } = values;
        const [fromDate, toDate] =
          dateRange?.length === 2
            ? [dateRange[0]?.format('YYYY-MM-DD'), dateRange[1]?.format('YYYY-MM-DD')]
            : [undefined, undefined];

        const model = REPORT_MODEL_MAP[selectedReportKey] || rest.model || '';

        const payload = {
          reportKey: selectedReportKey,
          ...rest,
          ...(fromDate && { from_date: fromDate }),
          ...(toDate && { to_date: toDate }),
          model,
          with_np: rest.with_np ?? false,
        };

        console.log('Generating report with payload:', payload);
        message.success(`Generating report for "${selectedReportKey}"`);
        setIsModalOpen(false);

        navigate('/report', {
          state: {
            reportKey: selectedReportKey,
            repType,
            filters: payload,
          },
        });
      })
      .catch((err) => {
        if (!err?.errorFields) handleError(err, { redirect: false });
      });
  };
  return (
    <>
      <PageTitle>{breadcrumbName}</PageTitle>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <ReportSelector repType={repType} onSelect={handleReportSelect} />
          <Modal
            title="Filter Report"
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onOk={handleGenerate}
            okText="Generate Report"
            width={800}
            bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}
          >
            {loading ? (
              <div style={{ textAlign: 'center' }}>
                <Spin size="large" style={{ marginBottom: '40px' }} />
                <p>
                  <b>Loading filters...</b>
                </p>
              </div>
            ) : (
              <ReportFilters reportKey={selectedReportKey} form={form} filterOptions={filterOptions} />
            )}
          </Modal>
        </Col>
      </Row>
    </>
  );
};

export default AllReportTypes;

// Utility function to format API data into Select-compatible options
function transformApiToFilterOptions(apiData: any) {
  return {
    division_options: apiData.division_options || [],
    departmentOptions: apiData.department_options || [],
    department_type_options: apiData.department_type_options || [],
    type_options: apiData.type_options || [],
    lab_service_type_options: apiData.lab_service_type_options || [],
    lab_service_category_options: apiData.lab_service_category_options || [],
    lab_sample_options: apiData.lab_sample_options || [],
    invoice_status_option: apiData.invoice_status_option || [],
    date_related_to_option: apiData.date_related_to_option || [],
    lab_count_by_options: apiData.lab_count_by_options || [],
    pr_category_options: apiData.pr_category_options || [],
    parent_pr_category_options: apiData.root_pr_category_options || [],
    product_category_options: apiData.product_category_options || [],
    import_types_options: apiData.import_types_options || [],
    export_types_options: apiData.export_types_options || [],
    level_options: apiData.level_options || [],
    payment_method_options: apiData.payment_method_options || [],
    cashier_options: apiData.cashier_options || [],
    cashier_location_options: apiData.cashier_location_options || [],
  };
}
